import { Home, CalendarRange, TrendingUp, Sun, Lightbulb, FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { H, YearsChart } from "./BehaviorAnalysis";
import { useAiJob, AiLoading, AiError } from "./AiJob";
import { dtt, nf } from "./shared";

const Split = ({ r, label }) => {
  const tot = r.consumo || 1;
  const parts = [["FV diretto", r.da_fv_diretto, "#C17F59"], ["Batteria", r.da_batteria, "#22c55e"], ["Rete", r.da_rete, "#c084fc"]];
  return (
    <div className="space-y-1.5 rounded-xl border border-neutral-800 p-3" data-testid={`consumi-split-${label}`}>
      <div className="flex items-baseline justify-between text-xs"><span className="font-bold text-white">{label}</span><span className="font-mono text-white">{nf(r.consumo)} kWh</span></div>
      <div className="flex h-2 overflow-hidden rounded-full bg-neutral-800">{parts.map(([l, v, c]) => <div key={l} style={{ width: `${((v || 0) / tot) * 100}%`, background: c }} />)}</div>
      <div className="flex flex-wrap gap-x-3 text-[11px] text-neutral-400">{parts.map(([l, v, c]) => <span key={l}><span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ background: c }} />{l} {nf(v)} kWh</span>)}
        {r.coperto_fv_pct != null && <span className="text-white">Coperto da FV {r.coperto_fv_pct}%</span>}</div>
    </div>
  );
};

const Body = ({ d, accent }) => {
  const r = d.report || {};
  const s = d.stats || {};
  if (!s.disponibile) return <p className="text-sm text-neutral-300" data-testid="consumi-non-disponibili">{r.sintesi}</p>;
  const t = s.totale_periodo || {};
  const anni = [{ anno: s.anno - 1, mesi: Object.fromEntries(s.mesi.map((m) => [m.mese, m.anno_prec.consumo])) }, { anno: s.anno, mesi: Object.fromEntries(s.mesi.map((m) => [m.mese, m.anno.consumo])) }];
  return (
    <div className="space-y-5" data-testid="consumi-body">
      <p className="text-sm leading-relaxed text-neutral-200" data-testid="consumi-sintesi">{r.sintesi}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Split r={s.mese_corrente} label={`${s.mese_corrente.mese} ${s.anno} (in corso)`} />
        <Split r={s.ultimo_mese_completo} label={`${s.ultimo_mese_completo.mese} ${s.anno}`} />
      </div>
      {r.mese && <div className="space-y-1"><H icon={Home}>Il mese</H><p className="text-sm text-neutral-300">{r.mese}</p></div>}
      <div className="space-y-2"><H icon={CalendarRange}>Consumi mese per mese</H>
        <YearsChart anni={anni} field="mesi" accent="#60a5fa" testid="consumi-chart" />
        {r.andamento_mesi && <p className="text-sm text-neutral-300">{r.andamento_mesi}</p>}</div>
      <div className="space-y-1" data-testid="consumi-vs-anno"><H icon={TrendingUp}>Rispetto all'anno precedente ({s.periodo})</H>
        <p className="text-xs text-neutral-400">{nf(t.consumo, 0)} kWh vs {nf(t.consumo_anno_prec, 0)} kWh{t.variazione_pct != null && <b className="ml-1" style={{ color: t.variazione_pct > 0 ? "#ef4444" : "#22c55e" }}>({t.variazione_pct > 0 ? "+" : ""}{t.variazione_pct}%)</b>}
          {t.autosufficienza_pct != null && ` · autosufficienza ${t.autosufficienza_pct}% (anno prec. ${t.autosufficienza_anno_prec_pct ?? "—"}%)`}</p>
        {r.vs_anno_prec && <p className="text-sm text-neutral-300">{r.vs_anno_prec}</p>}</div>
      {r.copertura_fv && <div className="space-y-1"><H icon={Sun}>Copertura dal fotovoltaico</H><p className="text-sm text-neutral-300">{r.copertura_fv}</p></div>}
      {!!r.consigli?.length && <div className="space-y-1" data-testid="consumi-consigli"><H icon={Lightbulb}>Consigli</H><ul className="list-disc space-y-1 pl-5 text-sm text-neutral-200">{r.consigli.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
      <p className="text-[11px] text-neutral-600">Analisi AI del {dtt(d.at)} su dati cloud FIER. Valida 24 ore.</p>
    </div>
  );
};


export function ConsumptionAnalysis({ base, params, plantId, open, onClose, accent, url, client, extra, pdf }) {
  const resUrl = url || `${base}/plants/${encodeURIComponent(plantId)}/consumi`;
  const [d, retry] = useAiJob(resUrl, params, open, client, extra);
  const ready = d && d.status !== "running" && d.status !== "error";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="consumi-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-lg" style={{ color: accent }}><Home className="h-5 w-5" />Comportamento dei consumi dell'utenza</DialogTitle>
          {pdf && ready && <button type="button" onClick={pdf} data-testid="consumi-pdf" className="inline-flex w-fit items-center gap-1 rounded-full border border-neutral-700 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59] hover:text-[#C17F59]"><FileDown className="h-3.5 w-3.5" />PDF</button>}
        </DialogHeader>
        {!d || d.status === "running" ? (
          <AiLoading d={d} text="Lettura dei consumi dal cloud FIER e analisi AI…" accent={accent} testid="consumi-loading" />
        ) : d.status === "error" ? <AiError error={d.error} onRetry={retry} accent={accent} testid="consumi-error" /> : <Body d={d} accent={accent} />}
      </DialogContent>
    </Dialog>
  );
}
